import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateUserProfile } from "../api/user";
import { updateAlertConfiguration } from "../redux/authSlice";

const Toggle = ({ enabled, onChange }) => {
	return (
		<div
			style={{
				...styles.toggleContainer,
				backgroundColor: enabled ? "#ffffff" : "#6b7280",
			}}
			onClick={onChange}>
			<div
				style={{
					...styles.toggleThumb,
					backgroundColor: enabled ? "#4D63AC" : "white",
					transform: enabled ? "translateX(20px)" : "translateX(2px)",
				}}
			/>
		</div>
	);
};

// Country codes with flags
const countryCodes = [
	{ code: "1", flag: "🇺🇸", country: "US" },
	{ code: "1", flag: "🇨🇦", country: "CA" },
	{ code: "44", flag: "🇬🇧", country: "UK" },
	{ code: "49", flag: "🇩🇪", country: "DE" },
	{ code: "33", flag: "🇫🇷", country: "FR" },
	{ code: "81", flag: "🇯🇵", country: "JP" },
	{ code: "86", flag: "🇨🇳", country: "CN" },
	{ code: "91", flag: "🇮🇳", country: "IN" },
	{ code: "61", flag: "🇦🇺", country: "AU" },
	{ code: "55", flag: "🇧🇷", country: "BR" },
	{ code: "92", flag: "🇵🇰", country: "PK" },
];

const CountryCodeDropdown = ({ countryCode, onChange }) => {
	const [isOpen, setIsOpen] = useState(false);
	const selectedCountry = countryCodes.find(c => c.code === countryCode) || countryCodes[0];

	return (
		<div style={styles.dropdownContainer}>
			<div style={styles.dropdownSelected} onClick={() => setIsOpen(!isOpen)}>
				<span style={styles.flagIcon}>{selectedCountry.flag}</span>
				<span style={styles.countryCode}>+{selectedCountry.code}</span>
				<span style={styles.dropdownArrow}>▼</span>
			</div>
			{isOpen && (
				<div style={styles.dropdownMenu}>
					{countryCodes.map((country, index) => (
						<div
							key={index}
							style={styles.dropdownItem}
							onClick={() => {
								onChange(country.code);
								setIsOpen(false);
							}}>
							<span style={styles.flagIcon}>{country.flag}</span>
							<span>
								+{country.code} {country.country}
							</span>
						</div>
					))}
				</div>
			)}
		</div>
	);
};

const CustomPhoneNumberField = ({ countryCode, phoneNumber, onChange }) => {
	return (
		<div style={styles.phoneInputs}>
			<CountryCodeDropdown countryCode={countryCode} onChange={code => onChange({ countryCode: code, phoneNumber })} />
			<input
				type="text"
				placeholder="Phone Number"
				value={phoneNumber || ""}
				onChange={e => onChange({ countryCode, phoneNumber: e.target.value })}
				style={styles.phoneInput}
			/>
		</div>
	);
};

const AlertConfiguration = ({ user }) => {
	const { countryCode, phoneNumber, slackWebhookUrl } = user;
	const [editingSlack, setEditingSlack] = useState(false);
	const [editingPhone, setEditingPhone] = useState(false);
	const [loading, setLoading] = useState(false);
	const [slackEnabled, setSlackEnabled] = useState(!!slackWebhookUrl);
	const [phoneEnabled, setPhoneEnabled] = useState(!!(phoneNumber && countryCode));
	const [formData, setFormData] = useState({
		slackWebhook: slackWebhookUrl || "",
		countryCode: countryCode || "",
		phoneNumber: phoneNumber || "",
	});
	const dispatch = useDispatch();
	const token = useSelector(state => state.auth.token);

	const handleSlackSave = async () => {
		setLoading(true);
		try {
			await updateUserProfile(
				{
					slackWebhookUrl: formData.slackWebhook,
				},
				token
			);

			dispatch(
				updateAlertConfiguration({
					slackWebhookUrl: formData.slackWebhook,
				})
			);

			// Update Slack enabled state based on new data
			setSlackEnabled(!!formData.slackWebhook);
			setEditingSlack(false);
		} catch (error) {
			console.error("Error updating Slack configuration:", error);
			alert("Failed to update Slack configuration. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	const handlePhoneSave = async () => {
		setLoading(true);
		try {
			await updateUserProfile(
				{
					phoneNumber: formData.phoneNumber,
					countryCode: formData.countryCode,
				},
				token
			);

			dispatch(
				updateAlertConfiguration({
					countryCode: formData.countryCode,
					phoneNumber: formData.phoneNumber,
				})
			);

			// Update phone enabled state based on new data
			setPhoneEnabled(!!(formData.phoneNumber && formData.countryCode));
			setEditingPhone(false);
		} catch (error) {
			console.error("Error updating phone configuration:", error);
			alert("Failed to update phone configuration. Please try again.");
		} finally {
			setLoading(false);
		}
	};

	return (
		<div style={styles.alertContainer}>
			<div style={styles.alertSectionTitle}>Alert destinations</div>

			<div style={styles.serviceCardsContainer}>
				{/* Slack Card */}
				<div style={styles.serviceCard}>
					<div style={styles.serviceHeader}>
						<div style={styles.serviceInfo}>
							<div style={styles.serviceIconContainer}>
								<img src="https://a.slack-edge.com/fd21de4/marketing/img/nav/logo.svg" alt="Slack" style={styles.slackIcon} />
							</div>
							<div style={styles.serviceTextContainer}>
								<span style={styles.serviceText}>Slack</span>
								<div style={styles.serviceSubtext}>{slackWebhookUrl || ""}</div>
							</div>
						</div>
						<div style={styles.serviceActions}>
							{!editingSlack && (
								<button style={styles.editButton} onClick={() => setEditingSlack(true)}>
									<img src="./edit.png" alt="Edit" style={styles.editIcon} />
								</button>
							)}
							<Toggle enabled={slackEnabled} onChange={() => {}} />
						</div>
					</div>

					{editingSlack && (
						<div style={styles.editForm}>
							<input
								type="text"
								placeholder="Slack Webhook URL"
								value={formData.slackWebhook}
								onChange={e => setFormData({ ...formData, slackWebhook: e.target.value })}
								style={styles.editInput}
							/>
							<div style={styles.editActions}>
								<button onClick={handleSlackSave} disabled={loading} style={styles.saveButton}>
									{loading ? "..." : "Save"}
								</button>
								<button onClick={() => setEditingSlack(false)} style={styles.cancelButton}>
									Cancel
								</button>
							</div>
						</div>
					)}
				</div>

				{/* Phone Card */}
				<div style={styles.serviceCard}>
					<div style={styles.serviceHeader}>
						<div style={styles.serviceInfo}>
							<div style={styles.serviceIconContainer}>
								<img src="./phone.svg" alt="Phone" style={styles.phoneIcon} />
							</div>
							<div style={styles.serviceTextContainer}>
								<span style={styles.serviceText}>Phone</span>
								<div style={styles.serviceSubtext}>
									{(formData.phoneNumber && formData.countryCode) || (phoneNumber && countryCode) ? (
										<span>
											<span style={styles.flagIcon}>{countryCodes.find(c => c.code === (formData.countryCode || countryCode))?.flag || "🏳️"}</span>+
											{formData.countryCode || countryCode} {formData.phoneNumber || phoneNumber}
										</span>
									) : (
										""
									)}
								</div>
							</div>
						</div>
						<div style={styles.serviceActions}>
							{!editingPhone && (
								<button style={styles.editButton} onClick={() => setEditingPhone(true)}>
									<img src="./edit.png" alt="Edit" style={styles.editIcon} />
								</button>
							)}
							<Toggle enabled={phoneEnabled} onChange={() => {}} />
						</div>
					</div>

					{editingPhone && (
						<div style={styles.editForm}>
							<CustomPhoneNumberField
								countryCode={formData.countryCode}
								phoneNumber={formData.phoneNumber}
								onChange={value => {
									setFormData({
										...formData,
										countryCode: value.countryCode,
										phoneNumber: value.phoneNumber,
									});
								}}
							/>
							<div style={styles.editActions}>
								<button onClick={handlePhoneSave} disabled={loading} style={styles.saveButton}>
									{loading ? "..." : "Save"}
								</button>
								<button onClick={() => setEditingPhone(false)} style={styles.cancelButton}>
									Cancel
								</button>
							</div>
						</div>
					)}
				</div>
			</div>
		</div>
	);
};

const styles = {
	alertContainer: {
		width: "100%",
	},

	alertSectionTitle: {
		fontSize: "1.2rem",
		fontWeight: "600",
		color: "white",
		marginBottom: "1.5rem",
	},

	serviceCardsContainer: {
		display: "flex",
		flexDirection: "column",
		gap: "1rem",
	},

	serviceCard: {
		background: "transparent",
		borderRadius: "12px",
		padding: "1rem",
		border: "1px solid rgba(255, 255, 255, 0.2)",
	},

	serviceHeader: {
		display: "flex",
		justifyContent: "space-between",
		alignItems: "center", // Changed from flex-start to center
		minHeight: "40px",
	},

	serviceInfo: {
		flex: 1,
		display: "flex",
		alignItems: "center", // Changed from flex-start to center
		gap: "0.75rem",
	},

	serviceIconContainer: {
		width: "32px",
		height: "32px",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		flexShrink: 0,
	},

	serviceTextContainer: {
		display: "flex",
		flexDirection: "column",
		gap: "0.25rem",
		minWidth: 0,
	},

	serviceText: {
		fontSize: "1rem",
		fontWeight: "600",
		color: "white",
		lineHeight: "1.2",
	},

	serviceSubtext: {
		fontSize: "0.875rem",
		color: "rgba(255, 255, 255, 0.7)",
		fontWeight: "400",
		letterSpacing: "0.025em",
		lineHeight: "1.25",
		fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
		wordBreak: "break-all",
		borderBottom: "1px solid rgba(255, 255, 255, 0.5)",
		paddingBottom: "2px",
		display: "block",
		width: "100%",
	},

	slackIcon: {
		width: "24px",
		height: "24px",
		objectFit: "contain",
	},

	phoneIcon: {
		width: "18px",
		height: "18px",
		filter: "brightness(0) invert(1)",
	},

	flagIcon: {
		fontSize: "1rem",
		marginRight: "0.25rem",
	},

	serviceActions: {
		display: "flex",
		alignItems: "center",
		gap: "0.75rem",
	},

	editButton: {
		background: "transparent",
		border: "none",
		cursor: "pointer",
		fontSize: "1rem",
		padding: "0.25rem",
		color: "rgba(255, 255, 255, 0.7)",
		transition: "color 0.2s ease",
		":hover": {
			color: "white",
		},
	},

	editIcon: {
		width: "16px",
		height: "16px",
		objectFit: "contain",
		filter: "brightness(0) invert(1)",
	},

	toggleContainer: {
		width: "44px",
		height: "24px",
		borderRadius: "12px",
		position: "relative",
		cursor: "default", // Changed from pointer to default
		transition: "background-color 0.2s ease",
	},

	toggleThumb: {
		width: "20px",
		height: "20px",
		borderRadius: "50%",
		position: "absolute",
		top: "2px",
		transition: "transform 0.2s ease, background-color 0.2s ease",
	},

	editForm: {
		marginTop: "1rem",
		paddingTop: "1rem",
		borderTop: "1px solid rgba(255, 255, 255, 0.1)",
	},

	editInput: {
		width: "100%",
		padding: "0.75rem 0",
		border: "none",
		borderBottom: "2px solid rgba(255, 255, 255, 0.3)",
		background: "transparent",
		color: "white",
		fontSize: "0.875rem",
		fontFamily: "inherit",
		marginBottom: "0.75rem",
		boxSizing: "border-box",
		outline: "none",
		transition: "border-bottom-color 0.2s ease",
	},

	phoneInputs: {
		display: "flex",
		gap: "1rem",
		marginBottom: "0.75rem",
	},

	// Country code dropdown styles
	dropdownContainer: {
		position: "relative",
		minWidth: "120px",
	},

	dropdownSelected: {
		display: "flex",
		alignItems: "center",
		gap: "0.5rem",
		padding: "0.75rem 0",
		borderBottom: "2px solid rgba(255, 255, 255, 0.3)",
		cursor: "pointer",
		background: "transparent",
		color: "white",
		fontSize: "0.875rem",
		transition: "border-bottom-color 0.2s ease",
	},

	countryCode: {
		flex: 1,
	},

	dropdownArrow: {
		fontSize: "0.75rem",
		color: "rgba(255, 255, 255, 0.6)",
	},

	dropdownMenu: {
		position: "absolute",
		top: "100%",
		left: 0,
		right: 0,
		background: "rgba(30, 30, 60, 0.95)",
		border: "1px solid rgba(255, 255, 255, 0.2)",
		borderRadius: "8px",
		maxHeight: "200px",
		overflowY: "auto",
		zIndex: 1000,
		backdropFilter: "blur(10px)",
	},

	dropdownItem: {
		display: "flex",
		alignItems: "center",
		gap: "0.5rem",
		padding: "0.75rem",
		cursor: "pointer",
		color: "white",
		fontSize: "0.875rem",
		transition: "background-color 0.2s ease",
		":hover": {
			backgroundColor: "rgba(255, 255, 255, 0.1)",
		},
	},

	phoneInput: {
		flex: 1,
		padding: "0.75rem 0",
		border: "none",
		borderBottom: "2px solid rgba(255, 255, 255, 0.3)",
		background: "transparent",
		color: "white",
		fontSize: "0.875rem",
		fontFamily: "inherit",
		boxSizing: "border-box",
		outline: "none",
		transition: "border-bottom-color 0.2s ease",
	},

	editActions: {
		display: "flex",
		gap: "0.5rem",
	},

	saveButton: {
		padding: "0.5rem 1rem",
		borderRadius: "6px",
		border: "none",
		background: "rgba(255, 255, 255, 0.2)",
		color: "white",
		fontSize: "0.875rem",
		cursor: "pointer",
		fontFamily: "inherit",
		fontWeight: "500",
	},

	cancelButton: {
		padding: "0.5rem 1rem",
		borderRadius: "6px",
		border: "1px solid rgba(255, 255, 255, 0.2)",
		background: "transparent",
		color: "white",
		fontSize: "0.875rem",
		cursor: "pointer",
		fontFamily: "inherit",
		fontWeight: "500",
	},
};

// Add CSS for placeholder text and input focus states
const additionalCSS = `
input::placeholder {
	color: rgba(255, 255, 255, 0.5) !important;
}

input:focus {
	border-bottom-color: #60a5fa !important;
}
`;

// Inject the CSS
if (typeof document !== "undefined") {
	const style = document.createElement("style");
	style.textContent = additionalCSS;
	document.head.appendChild(style);
}

export default AlertConfiguration;
